import { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import axios from 'axios';
import { format } from 'date-fns';
import { Calendar as CalendarIcon, Loader2, CheckCircle2, AlertCircle, Users, MapPin, User, Mail, Phone, MessageSquare } from 'lucide-react';
import { Calendar } from './ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { cn } from '../lib/utils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AvailabilityForm = ({ onClose }) => {
  const { t } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [date, setDate] = useState(null);

  const [formData, setFormData] = useState({
    vehicle_type: 'sprinter',
    pickup_time: '',
    pickup_location: '',
    dropoff_location: '',
    passengers: 8,
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    message: '',
  });

  const timeSlots = [];
  for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += 30) {
      timeSlots.push(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`);
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!date || !formData.pickup_time || !formData.customer_name || !formData.customer_email || !formData.customer_phone) {
      return;
    }

    setLoading(true);
    try {
      const requestData = {
        ...formData,
        pickup_date: format(date, 'yyyy-MM-dd'),
      };

      await axios.post(`${API}/availability-request`, requestData);
      setSuccess(true);
    } catch (error) {
      console.error('Failed to submit availability request:', error);
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="text-center py-8" data-testid="availability-success">
        <CheckCircle2 className="w-16 h-16 text-[#D4AF37] mx-auto mb-4" />
        <h3 
          className="text-2xl text-white mb-3"
          style={{ fontFamily: 'Cormorant Garamond, serif' }}
        >
          {t('availability_success')}
        </h3>
        <p className="text-[#A1A1AA] mb-6">
          {t('availability_success_msg')}
        </p>
        {onClose && (
          <button
            onClick={onClose}
            className="px-6 py-2 border border-white/20 text-white hover:bg-white/5 transition-all duration-300"
            data-testid="close-availability-form"
          >
            {t('close')}
          </button>
        )}
      </div>
    );
  }

  return (
    <div data-testid="availability-form">
      {/* Header Info */}
      <div className="flex items-start gap-3 mb-6 p-4 bg-[#0A0A0A] border border-[#D4AF37]/30">
        <AlertCircle className="w-5 h-5 text-[#D4AF37] flex-shrink-0 mt-0.5" />
        <div>
          <h4 className="text-white font-medium mb-1">{t('availability_title')}</h4>
          <p className="text-sm text-[#A1A1AA]">{t('availability_subtitle')}</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Date */}
          <div>
            <label className="block text-sm text-[#A1A1AA] mb-2">
              {t('booking_pickup_date')} *
            </label>
            <Popover>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  className={cn(
                    "w-full flex items-center justify-between bg-[#0A0A0A] border border-white/10 text-white px-4 py-3 hover:border-[#D4AF37]/50 transition-colors",
                    !date && "text-[#A1A1AA]"
                  )}
                  data-testid="availability-date"
                >
                  {date ? format(date, 'PPP') : t('select')}
                  <CalendarIcon className="w-4 h-4 text-[#D4AF37]" />
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0 bg-[#121212] border-white/10" align="start">
                <Calendar
                  mode="single"
                  selected={date}
                  onSelect={setDate}
                  disabled={(date) => date < new Date()}
                  initialFocus
                  className="bg-[#121212]"
                />
              </PopoverContent>
            </Popover>
          </div>

          {/* Time */}
          <div>
            <label className="block text-sm text-[#A1A1AA] mb-2">
              {t('booking_pickup_time')} *
            </label>
            <Select
              value={formData.pickup_time}
              onValueChange={(value) => setFormData({ ...formData, pickup_time: value })}
            >
              <SelectTrigger className="w-full bg-[#0A0A0A] border-white/10 text-white" data-testid="availability-time">
                <SelectValue placeholder={t('select')} />
              </SelectTrigger>
              <SelectContent className="bg-[#121212] border-white/10 max-h-64">
                {timeSlots.map((time) => (
                  <SelectItem key={time} value={time} className="text-white">
                    {time}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Passengers */}
          <div>
            <label className="block text-sm text-[#A1A1AA] mb-2">
              <Users className="w-4 h-4 inline mr-1" />
              {t('calc_passengers')}
            </label>
            <Select
              value={String(formData.passengers)}
              onValueChange={(value) => setFormData({ ...formData, passengers: parseInt(value) })}
            >
              <SelectTrigger className="w-full bg-[#0A0A0A] border-white/10 text-white" data-testid="availability-passengers">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#121212] border-white/10">
                {[1, 2, 3, 4, 5, 6, 7, 8].map((num) => (
                  <SelectItem key={num} value={String(num)} className="text-white">
                    {num}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Pickup Location */}
          <div>
            <label className="block text-sm text-[#A1A1AA] mb-2">
              <MapPin className="w-4 h-4 inline mr-1" />
              {t('booking_pickup_location')} *
            </label>
            <input
              type="text"
              value={formData.pickup_location}
              onChange={(e) => setFormData({ ...formData, pickup_location: e.target.value })}
              className="w-full bg-[#0A0A0A] border border-white/10 text-white px-4 py-3 focus:border-[#D4AF37] outline-none"
              required
              data-testid="availability-pickup"
            />
          </div>

          {/* Dropoff Location */}
          <div className="md:col-span-2">
            <label className="block text-sm text-[#A1A1AA] mb-2">
              {t('booking_dropoff_location')} *
            </label>
            <input
              type="text"
              value={formData.dropoff_location}
              onChange={(e) => setFormData({ ...formData, dropoff_location: e.target.value })}
              className="w-full bg-[#0A0A0A] border border-white/10 text-white px-4 py-3 focus:border-[#D4AF37] outline-none"
              required
              data-testid="availability-dropoff"
            />
          </div>

          {/* Customer Name */}
          <div>
            <label className="block text-sm text-[#A1A1AA] mb-2">
              <User className="w-4 h-4 inline mr-1" />
              {t('booking_name')} *
            </label>
            <input
              type="text"
              value={formData.customer_name}
              onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
              className="w-full bg-[#0A0A0A] border border-white/10 text-white px-4 py-3 focus:border-[#D4AF37] outline-none"
              required
              data-testid="availability-name"
            />
          </div>

          {/* Customer Phone */}
          <div>
            <label className="block text-sm text-[#A1A1AA] mb-2">
              <Phone className="w-4 h-4 inline mr-1" />
              {t('booking_phone')} *
            </label>
            <input
              type="tel"
              value={formData.customer_phone}
              onChange={(e) => setFormData({ ...formData, customer_phone: e.target.value })}
              className="w-full bg-[#0A0A0A] border border-white/10 text-white px-4 py-3 focus:border-[#D4AF37] outline-none"
              required
              data-testid="availability-phone"
            />
          </div>

          {/* Customer Email */}
          <div className="md:col-span-2">
            <label className="block text-sm text-[#A1A1AA] mb-2">
              <Mail className="w-4 h-4 inline mr-1" />
              {t('booking_email')} *
            </label>
            <input
              type="email"
              value={formData.customer_email}
              onChange={(e) => setFormData({ ...formData, customer_email: e.target.value })}
              className="w-full bg-[#0A0A0A] border border-white/10 text-white px-4 py-3 focus:border-[#D4AF37] outline-none"
              required
              data-testid="availability-email"
            />
          </div>

          {/* Message */}
          <div className="md:col-span-2">
            <label className="block text-sm text-[#A1A1AA] mb-2">
              <MessageSquare className="w-4 h-4 inline mr-1" />
              {t('availability_message')}
            </label>
            <textarea
              value={formData.message}
              onChange={(e) => setFormData({ ...formData, message: e.target.value })}
              rows={3}
              className="w-full bg-[#0A0A0A] border border-white/10 text-white px-4 py-3 focus:border-[#D4AF37] outline-none resize-none"
              data-testid="availability-message"
            />
          </div>
        </div>

        {/* Submit Button */}
        <button
          type="submit"
          disabled={loading}
          className="w-full py-4 bg-[#D4AF37] text-black font-semibold text-sm uppercase tracking-wider hover:bg-[#C5A059] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          data-testid="submit-availability-button"
        >
          {loading ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : (
            t('availability_submit')
          )}
        </button>
      </form>
    </div>
  );
};

export default AvailabilityForm;
